import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Send, User, MessageSquare, Paperclip, FileText, X, Download, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { groupMessageService } from '@/services/groupMessageService'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import { useMessagingSocket } from '@/contexts/MessagingSocketContext'
import { GroupRoom, GroupMessage } from '@/types/group'
import { UserRole } from '@/types/user'
import { MessagingSocketEvent, IMessageNewPayload } from '@/types/messaging'

const PAGE_SIZE = 30
const MAX_FILES = 5
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = [
  'image/',
  'application/pdf',
  'text/',
  'application/msword',
  'application/vnd.openxmlformats-officedocument',
  'application/zip'
]

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface GroupChatViewProps {
  group: GroupRoom
}

const GroupChatView: React.FC<GroupChatViewProps> = ({ group }) => {
  const { user } = useAuth()
  const isAppAdmin = user?.role === UserRole.ADMIN
  const { showToast } = useToast()
  const { joinRoom, leaveRoom, on } = useMessagingSocket()
  const [messages, setMessages] = useState<GroupMessage[]>([])
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [content, setContent] = useState('')
  const [sending, setSending] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const listRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const skipRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setMessages([])
    skipRef.current = 0

    groupMessageService
      .getMessages(group.id, { limit: PAGE_SIZE, skip: 0 })
      .then((page) => {
        if (cancelled) return
        setMessages([...page].reverse())
        setHasMore(page.length === PAGE_SIZE)
        skipRef.current = page.length
      })
      .catch((error) => {
        showToast({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to load messages'
        })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [group.id, showToast])

  useEffect(() => {
    joinRoom(group.id)
    return () => {
      leaveRoom(group.id)
    }
  }, [group.id, joinRoom, leaveRoom])

  useEffect(() => {
    groupMessageService.markAsRead(group.id).catch(() => {
      // Best-effort — the badge is cleared optimistically in the inbox regardless.
    })
  }, [group.id])

  useEffect(() => {
    return on<IMessageNewPayload>(MessagingSocketEvent.MESSAGE_NEW, (payload) => {
      const incoming = payload.message
      if (incoming.chatRoomId !== group.id) return
      setMessages((prev) => {
        if (prev.some((m) => m.id === incoming.id)) return prev
        return [...prev, incoming as unknown as GroupMessage]
      })
    })
  }, [group.id, on])

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return

    const container = listRef.current
    const prevScrollHeight = container?.scrollHeight ?? 0

    try {
      setLoadingMore(true)
      const page = await groupMessageService.getMessages(group.id, {
        limit: PAGE_SIZE,
        skip: skipRef.current
      })
      setMessages((prev) => [...[...page].reverse(), ...prev])
      setHasMore(page.length === PAGE_SIZE)
      skipRef.current += page.length

      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight
        }
      })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load more messages'
      })
    } finally {
      setLoadingMore(false)
    }
  }, [group.id, hasMore, loadingMore, showToast])

  const handleScroll = () => {
    if (listRef.current && listRef.current.scrollTop <= 0) {
      handleLoadMore()
    }
  }

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `${file.name} exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`
    }
    if (!ALLOWED_TYPES.some((type) => file.type.startsWith(type))) {
      return `${file.name} has an unsupported file type`
    }
    return null
  }

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return

    const newFiles: File[] = []
    let firstError: string | null = null

    Array.from(selectedFiles).forEach((file) => {
      if (attachedFiles.length + newFiles.length >= MAX_FILES) {
        firstError = firstError ?? `Maximum ${MAX_FILES} files allowed`
        return
      }
      const error = validateFile(file)
      if (error) {
        firstError = firstError ?? error
      } else {
        newFiles.push(file)
      }
    })

    if (firstError) {
      showToast({ type: 'error', message: firstError })
    }
    if (newFiles.length > 0) {
      setAttachedFiles((prev) => [...prev, ...newFiles])
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeAttachedFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = content.trim()
    if ((!trimmed && attachedFiles.length === 0) || sending) return

    try {
      setSending(true)
      const message = await groupMessageService.sendMessage(
        group.id,
        attachedFiles.length > 0
          ? { content: trimmed, attachments: attachedFiles }
          : { content: trimmed }
      )
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]))
      setContent('')
      setAttachedFiles([])
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to send message'
      })
    } finally {
      setSending(false)
    }
  }

  const handleDeleteMessage = async (messageId: string) => {
    try {
      setDeletingMessageId(messageId)
      await groupMessageService.deleteMessage(group.id, messageId)
      setMessages((prev) => prev.filter((m) => m.id !== messageId))
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete message'
      })
    } finally {
      setDeletingMessageId(null)
    }
  }

  const formatMessageTime = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  }

  return (
    <div className="flex flex-col h-full">
      <div
        ref={listRef}
        onScroll={handleScroll}
        data-testid="group-message-list"
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
      >
        {loadingMore && (
          <div className="flex justify-center py-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-500">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwnMessage = message.senderId === user?.id
            return (
              <div key={message.id} data-testid="group-message" className="flex items-start gap-3">
                <Link
                  to={`/users/${message.senderId}`}
                  className="w-9 h-9 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0"
                >
                  {message.sender.profilePicture ? (
                    <img
                      src={message.sender.profilePicture}
                      alt={message.sender.firstName}
                      className="w-9 h-9 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-4 h-4 text-gray-500" />
                  )}
                </Link>
                <div
                  className={`flex-1 min-w-0 rounded-lg px-3 py-2 ${
                    isOwnMessage ? 'bg-primary-50' : 'bg-white border border-gray-200'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <Link
                      to={`/users/${message.senderId}`}
                      className="font-medium text-sm text-gray-900 hover:underline"
                    >
                      {message.sender.firstName} {message.sender.lastName}
                    </Link>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">
                        {formatMessageTime(message.createdAt)}
                      </span>
                      {isAppAdmin && (
                        <button
                          type="button"
                          onClick={() => handleDeleteMessage(message.id)}
                          disabled={deletingMessageId === message.id}
                          aria-label="Delete message"
                          className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  {message.content && <p className="text-gray-900 break-words">{message.content}</p>}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="mt-2 space-y-2" data-testid="message-attachments">
                      {message.attachments.map((attachment, index) =>
                        attachment.mimeType.startsWith('image/') ? (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="attachment-image-link"
                          >
                            <img
                              src={attachment.url}
                              alt={attachment.filename}
                              className="max-w-xs max-h-60 rounded-lg border border-gray-200 object-cover"
                            />
                          </a>
                        ) : (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                            download={attachment.filename}
                            className="flex items-center gap-2 text-sm text-primary-600 hover:underline"
                          >
                            <FileText className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{attachment.filename}</span>
                            <Download className="w-3.5 h-3.5 flex-shrink-0" />
                          </a>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      <form onSubmit={handleSend} className="p-4 border-t border-gray-200 bg-white">
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="staged-attachments">
            {attachedFiles.map((file, index) => (
              <div
                key={index}
                data-testid="staged-attachment"
                className="flex items-center gap-2 pl-2 pr-1 py-1 bg-gray-100 rounded-lg text-sm"
              >
                <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="truncate max-w-[10rem]">{file.name}</span>
                <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => removeAttachedFile(index)}
                  disabled={sending}
                  className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
            disabled={sending || attachedFiles.length >= MAX_FILES}
            accept="image/*,.pdf,.doc,.docx,.txt,.zip"
            data-testid="attachment-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || attachedFiles.length >= MAX_FILES}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Attach file"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <Input
            type="text"
            placeholder="Type a message..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="flex-1"
            disabled={sending}
          />
          <Button
            type="submit"
            variant="primary"
            disabled={(!content.trim() && attachedFiles.length === 0) || sending}
            isLoading={sending}
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  )
}

export default GroupChatView
